import {Observable} from 'rxjs/Observable';
import 'rxjs/add/observable/of';
import 'rxjs/add/operator/map';
import 'rxjs/add/operator/debounceTime';
import 'rxjs/add/operator/mergeMap';
import 'rxjs/add/operator/switchMap';
import 'rxjs/add/operator/switch';
import 'rxjs/add/operator/catch';
import 'rxjs/add/operator/do';
import 'rxjs/add/operator/bufferCount';
import 'rxjs/add/operator/concat';
import 'rxjs/add/operator/groupBy';
import 'rxjs/add/operator/throttleTime';
import includes from 'lodash/includes';
import * as ResourceApi from 'api/resources';
import {
  createdResourceAction,
  deletedResourceAction,
  getResourcePageAction,
  receivedAllResourcesAction,
  receivedResourceAction,
  receivedResourceInfo,
  receivedResourcePageAction,
  resourceFieldSavedAction,
  saveResourceFieldErrorAction,
  sentSaveResourceFieldAction,
  saveResourceChangelistSuccess,
  saveResourceChangelistError,
  synchronizedResourceAction,
  Types as ResourceActions
} from './actions';
import {createResourcePageSelector} from './selectors';
import {Types as ViewOptionsActions} from 'viewOptions/actions';
import {createResourceFilterOptionsSelector, createResourceSortOptionsSelector} from 'viewOptions/selectors';
import {getSearchableUserResources} from 'pages/UserManagement/utils';
import * as BreakActions from 'pages/EditBreak/actions';
import * as RewardsActions from 'pages/Rewards/actions';
import {breakImagesPageName} from "pages/EditBreak/utils";
import {isNoU} from "../utils/DBUtils";
import Config from "Config";
import * as UserActions from 'pages/Login/actions';

const createResourceEpic = (action$) => action$.ofType(ResourceActions.CREATE_RESOURCE)
    .groupBy(action => action.resourceType)
    .mergeMap((group) => group.throttleTime(100) // Just enough time to prevent accidental double clicks
        .mergeMap(action => Observable.of(action)
            .concat(ResourceApi.create(action.resourceType, action.data, action.resourceIds))
            .bufferCount(2)
            .map(([action, {response: {object, modifiedBreak}}]) => createdResourceAction(object, action.rootResource, action.resourceIds, action.resourcePath, action.fieldPath, modifiedBreak))
            .catch(err => Observable.of({type: "ERROR", error: err})))
    );

const synchronizeResourceEpic = action$ => action$.ofType(ResourceActions.SYNCHRONIZE_RESOURCE)
    .groupBy(action => action.platform)
    .mergeMap(group => group.throttleTime(100)
        .mergeMap(action => Observable.of(action)
            .concat(ResourceApi.synchronize(action.platform, action.resourceId))
            .bufferCount(2)
            .map(([action, response]) => synchronizedResourceAction(
                response.response.object,
                action.platform,
                action.resourceId
            ))
            .catch(err => Observable.of({type: 'ERROR', error: err})))
    );

const deleteResourceEpic = (action$) => action$.ofType(ResourceActions.DELETE_RESOURCE)
    .groupBy(action => action.resourceType)
    .mergeMap((group) => group.throttleTime(100) // Just enough time to prevent accidental double clicks
        .mergeMap(action => Observable.of(action)
            .do(action => console.log('delete resource', action))
            .concat(ResourceApi.deleteResource(action.resourceType, action.resource.resourceId.id, action.resourceIds, action.data))
            .bufferCount(2)
            .map(([action, {response: {modifiedBreak}}]) => deletedResourceAction(action.resource, action.rootResource, action.resourceIds, action.resourcePath, action.fieldPath, action.data, modifiedBreak))
            .catch(err => Observable.of({type: "ERROR", error: err})))
    );

const saveResourceFieldEpic = (action$) => action$.ofType(ResourceActions.SAVE_RESOURCE_FIELD)
    .groupBy(action => action.resourceType + action.id + action.field) // Make observable path for each field per resource
    .mergeMap((group) => group
        .debounceTime(Config.SAVE_FIELD_DEBOUNCE_TIME) // Only send latest save call
        .map(action => {
          // don't send api save if there are errors
          return isNoU(action.errors) || action.errors.length === 0 ? sentSaveResourceFieldAction(action) : saveResourceFieldErrorAction(action.id, action.field, action.value, {
            errorCode: "INVALID_VALUE",
            failedValidations: action.errors.map(item => ({name: item.type})),
            sectionKeyPath: action.sectionKeyPath
          }, action.rootResourceId, action.fieldPath)
        })
    );

const sentSaveResourceFieldEpic = (action$) => action$.ofType(ResourceActions.SENT_SAVE_RESOURCE_FIELD)
    .mergeMap(action => ResourceApi.saveField(
        action.resourceType,
        action.id,
        action.field,
        action.value,
        action.resourceParents,
        action.args)
        .mergeMap(({response: {object, modifiedBreak}}) => Observable.of(
            resourceFieldSavedAction(action, object, modifiedBreak),
            resourceFieldSavedAction(action, object, modifiedBreak, action.resourceType)
        ))
        .catch((err) => {
            return Observable.of(
                // Api error handled differently from client errors
                // If xhr property exists, should be an AjaxError
                !isNoU(err.xhr) ? saveResourceFieldErrorAction( // Emit error action to store error object
                    action.id,
                    action.field,
                    action.value,
                    err.response,
                    action.rootResourceId,
                    action.fieldPath
                ) : { // Not an api error
                    type: "ERROR",
                    error: err
                }
            );
        })
    );

const resourceFieldSavedEpic = (action$) => action$.ofType(ResourceActions.RESOURCE_FIELD_SAVED)
    .mergeMap((action) => Observable.of(...action.postActions));

const saveResourceChangelistEpic = action$ => action$.ofType(ResourceActions.SAVE_RESOURCE_CHANGELIST_SUBMIT)
  .mergeMap(action => {
    const {resourceType, id, changelist, rootResourceId, resourcePath, parentIds, args} = action;
    const responseHandlerArgs = [resourceType, id, changelist, rootResourceId, resourcePath];

    return ResourceApi.saveChangelist(resourceType, id, changelist, parentIds, args)
      .map(xhr => saveResourceChangelistSuccess(...responseHandlerArgs))
      .catch(error => {
        const errorHandlerArgs = responseHandlerArgs.concat(error);
        const action = saveResourceChangelistError(...errorHandlerArgs);

        return Observable.of(action);
      });
  });

const getResourcePageEpic = (action$, store) => action$.ofType(ResourceActions.GET_RESOURCE_PAGE, ViewOptionsActions.REMOVE_ALL_FILTERS_FOR_RESOURCE)
    .groupBy(action => action.pageId || action.resourceType)
    // Makes get page cancelable so only latest request continues
    .mergeMap(group => group
        .switchMap(action => {
                const state = store.getState();
                const getResourcePage = createResourcePageSelector(action.pageId || action.resourceType);
                const resourcePage = getResourcePage(state.resources);

                // Prefill empty options so they'll be passed to the response handler
                const count = action.count || resourcePage.count;
                const page = action.page || resourcePage.page;
                const sortOptions = action.sortOptions || createResourceSortOptionsSelector(action.resourceType)(state);
                const filterOptions = action.filterOptions || createResourceFilterOptionsSelector(action.resourceType)(state);

                // Create observable to output current action
                return Observable.of(Object.assign({}, action, {count, page, sortOptions, filterOptions}))
                // Combine action observable with observable to output api response
                    .concat(ResourceApi.getPage(action.resourceType, count, page, sortOptions, filterOptions))
                    .bufferCount(2) // Save action and response to be output at the same time to the next stage
                    .mergeMap(([action, response]) => {
                      const actions = [
                        receivedResourcePageAction(
                          action.resourceType,
                          response.response,
                          action.sortOptions,
                          action.filterOptions,
                          action.pageId,
                          action.params
                        )
                      ];

                      if (action.pageId === breakImagesPageName) {
                        actions.push(BreakActions.receivedAllBreakImagesAction(response.response.object));
                      }

                      if (action.resourceType === ResourceApi.Types.StoreCatalog) {
                        actions.push(RewardsActions.receivedStoreCatalogAction());
                      }

                      return Observable.of(...actions);
                    })
                    .catch(err => Observable.of({type: "ERROR", error: err}));
            }
        ));

const getResourceEpic = (action$) => action$.ofType(ResourceActions.GET_RESOURCE)
    .mergeMap(action => Observable.of(action)
        .concat(ResourceApi.getById(action.resourceType, action.id))
        .bufferCount(2)
        .map(([action, response]) => receivedResourceAction(action.resourceType, response.response.object))
        .catch(err => Observable.of({type: "ERROR", error: err}))
    );

const getAllResourcesEpic = (action$, store) => action$.ofType(ResourceActions.GET_ALL_RESOURCES)
    .mergeMap(action => Observable.of(action)
        .concat(ResourceApi.getAll(action.resourceType, action.sortOptions, action.filterOptions))
        .bufferCount(2)
        .mergeMap(([action, response]) => {
            const actions = [
                receivedAllResourcesAction(action.resourceType, response.response.object),
                {
                    type: ResourceActions.RECEIVED_ALL_RESOURCES + "_" + action.resourceType.toUpperCase(),
                    resourceType: action.resourceType,
                    resources: response.response.object,
                    params: action.params || {}
                }
            ];

            if (action.resourceType === ResourceApi.Types.Image) {
                const state = store.getState();
                const usedByCurrentBreak = (action.filterOptions.filters || []).some(({field, value}) => (
                    field === "usedBy.id" && value === state.editBreak.lastBreakImagesId
                ));

                if (usedByCurrentBreak) {
                    actions.push(BreakActions.receivedAllBreakImagesAction(response.response.object));
                }
            }

            return Observable.of(...actions);
        })
        .catch(err => Observable.of({type: "ERROR", error: err}))
    );

// resourcePages that dispatch their own GET_RESOURCE_PAGE actions
const ignoreRedirectResources = [
  breakImagesPageName,
  ResourceApi.Types.Prize,
  ResourceApi.Types.PrizeEntry,
  ResourceApi.Types.StoreCatalog,
    ResourceApi.Types.User
];
// Removed so survey answers could be refreshed
// is this still necessary?
//.concat(getSearchableUserResources(userMgmtConfigs));

const filterActions = [
  ViewOptionsActions.ADD_FILTER_FOR_RESOURCE,
  ViewOptionsActions.REMOVE_FILTER_FOR_RESOURCE
];

const redirectToGetResourcePageEpic = action$ => action$.ofType(
    ViewOptionsActions.TOGGLE_SORT_FOR_RESOURCE,
    ResourceActions.UPDATE_PAGE_COUNT_FOR_RESOURCE,
    ResourceActions.UPDATE_PAGE_NUMBER_FOR_RESOURCE,
    ViewOptionsActions.ADD_FILTER_FOR_RESOURCE,
    ViewOptionsActions.REMOVE_FILTER_FOR_RESOURCE,
    ResourceActions.REFRESH_PAGE
)
    .filter(action => {
      if (action.resourceType === ResourceApi.Types.Prize) {
        // filtering on prize resource (for dashboard filters) is a special case
        return includes(filterActions, action.type);
      } else {
        // otherwise only proceed if this resource type is not on our ignore list
        return !includes(ignoreRedirectResources, action.resourceType);
      }
    })
    .map(action => {
        return getResourcePageAction(action.resourceType, null, null, null, null, action.pageId)
    });

const getResourceInfoEpic = action$ => action$.ofType(ResourceActions.GET_RESOURCE_INFO)
    .mergeMap(action => (action.resourceType ? ResourceApi.getAllResourceInfo() : ResourceApi.getResourceInfo(action.resourceType))
        .map(({response: {object}}) => receivedResourceInfo(object))
        .catch(err => Observable.of({type: "ERROR", error: err}))
    );

const authErrorEpic = action$ => action$.ofType("ERROR")
    .filter(action => action.error && action.error.response && action.error.response.errorCode === "REGISTRATION_REQUIRED")
    .map(() => UserActions.logoutSuccessAction());

export default [
    createResourceEpic,
    synchronizeResourceEpic,
    deleteResourceEpic,
    saveResourceFieldEpic,
    saveResourceChangelistEpic,
    getResourcePageEpic,
    sentSaveResourceFieldEpic,
    getResourceEpic,
    getAllResourcesEpic,
    redirectToGetResourcePageEpic,
    resourceFieldSavedEpic,
    authErrorEpic
];



// WEBPACK FOOTER //
// ./public/resources/epics.js